import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {finalize, of, take, tap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoDirective} from '@jsverse/transloco';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ArrowRightToLineIcon, LucideAngularModule} from 'lucide-angular';
import {toSignal} from '@angular/core/rxjs-interop';
import {AltchaComponent} from '@shared/altcha.component';
import {HostDTO} from '@domain/host/host.dto';
import {RouteGenerator} from '@core/route-generator';
import {UI_ROUTES} from '@core/ui.routes';
import {MemberApi} from '@domain/member/member.api';
import {ValidateMemberJoinRequest} from '@domain/member/member.dto';
import {SpinnerComponent} from '@shared/spinner/spinner.component';
import {ContactSupportComponent} from '@shared/content/contact-support/contact-support.component';

@Component({
  selector: 'app-auth-member-validate-join-host',
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    LucideAngularModule,
    AltchaComponent,
    SpinnerComponent,
    ContactSupportComponent
  ],
  templateUrl: './validate-join-host.component.html',
  styleUrl: './validate-join-host.component.scss'
})
export class ValidateJoinHostComponent implements OnInit {

  private static readonly VALIDATE_DELAY = 30 * 24 * 60 * 60_000; // 30 days
  private static readonly QUERY_PARAM_NOW = 'now';
  private static readonly QUERY_PARAM_DATA = 'data';
  private static readonly QUERY_PARAM_HASH = 'hash';
  private static readonly QUERY_PARAM_SECRET = 'secret';

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly data = toSignal(this.route.data);
  public readonly host = computed(() => (this.data()?.host as HostDTO) ?? null);
  private readonly memberApi = inject(MemberApi);

  public readonly icons = {
    join: ArrowRightToLineIcon
  };

  public readonly captchaOk = signal(false);
  public readonly validating = signal(false);
  public readonly validationOk = signal(false);
  public readonly validationError = signal(false);
  public readonly showInvalidLink = signal(false);
  public readonly showCaptcha = computed(() => this.host() && !this.captchaOk() && !this.showInvalidLink());
  public readonly disableJoinButton = computed(() => this.validating() || this.validationOk() || this.validationError());

  public readonly captchaForm = this.fb.group({
    captcha: ['', Validators.required]
  });

  public readonly routes = {
    dashboard: RouteGenerator.abs(UI_ROUTES.account.dashboard)
  };

  public ngOnInit(): void {
    const validationParams = this.getValidationParams();
    this.showInvalidLink.set(!validationParams);
  }

  public validateCaptcha(): void {
    this.captchaOk.set(true);
  }

  public validateJoin(): void {
    const validateRequest = this.getValidationRequest();
    if (!validateRequest) {
      return;
    }
    this.validating.set(true);
    this.memberApi.validateJoin(validateRequest).pipe(
      take(1),
      catchError(_err => {
        this.validationError.set(true);
        return of(false);
      }),
      tap(res => {
        if (res) {
          this.validationOk.set(true);
          this.router.navigate([this.routes.dashboard]).then(_ => {});
        } else {
          this.validationError.set(true);
        }
      }),
      finalize(() => {
        this.validating.set(false);
      })
    ).subscribe(_ => {});
  }

  private getDataParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateJoinHostComponent.QUERY_PARAM_DATA] ?? null;
  }

  private getHashParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateJoinHostComponent.QUERY_PARAM_HASH] ?? null;
  }

  private getSecretParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateJoinHostComponent.QUERY_PARAM_SECRET] ?? null;
  }

  private getStringTimeParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateJoinHostComponent.QUERY_PARAM_NOW] ?? null;
  }

  private getIntTime(): number | null {
    const time = this.getStringTimeParam();
    if (!time) {
      return null;
    }
    const num = parseInt(time, 10);
    if (isNaN(num)) {
      return null;
    }
    return num;
  }

  private isExpiredLink(): boolean {
    const t = this.getIntTime();
    if (!t) {
      return false;
    }
    const nowMillis = Date.now();
    const maxValidMillis = t + ValidateJoinHostComponent.VALIDATE_DELAY;
    return maxValidMillis < nowMillis;
  }

  private getValidationParams(): ValidateMemberJoinRequest | null {
    const time = this.getIntTime();
    const data = this.getDataParam();
    const secret = this.getSecretParam();
    const hash = this.getHashParam();
    return (time && data && secret && hash && !this.isExpiredLink())
      ? { data: data, secret: secret, now: time.toString(10), hash: hash }
      : null;
  }

  private getValidationRequest(): ValidateMemberJoinRequest | null {
    const captcha = this.captchaForm?.get('captcha')?.value ?? null;
    if (!captcha) {
      return null;
    }
    const params = this.getValidationParams();
    if (!params) {
      return null;
    }
    return {... params, captcha: captcha};
  }
}
